import {
  uploadFile,
  UploadFileData,
  UploadFileResponse,
  commitFileUpload,
  CommitFileUploadData,
  CommitFileUploadResponse,
  initiateFileUpload,
  InitiateFileUploadData,
  InitiateFileUploadResponse,
  getFileDetails,
  GetFileDetailsResponse,
  downloadUrl,
  DownloadUrlData,
  DownloadUrlResponse as _DownloadUrlResponse,
} from '@data-access/connect/tc-tcps';
import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';

export class FileApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  // Query for getting file details
  public getFileDetailsQuery(
    fileId: Accessor<string | undefined>
  ): UseQueryResult<GetFileDetailsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['file-details', fileId()],
      queryFn: async (): Promise<GetFileDetailsResponse> => {
        const id = fileId();
        if (!id) {
          throw new Error('File ID is required');
        }

        const response = await getFileDetails({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          path: { fileId: id },
        });

        if (response.error) {
          throw new Error('Failed to get file details');
        }

        return response.data!;
      },
      enabled: !!fileId(),
    }));
  }

  // Mutation for uploading a file
  public uploadFileMutation(): UseMutationResult<
    UploadFileResponse,
    Error,
    UploadFileData
  > {
    return useMutation(() => ({
      mutationFn: async (data: UploadFileData): Promise<UploadFileResponse> => {
        const response = await uploadFile({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to upload file');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate files list queries
        void this.queryClient.invalidateQueries({
          queryKey: ['files'],
        });
      },
    }));
  }

  // Mutation for initiating file upload
  public initiateFileUploadMutation(): UseMutationResult<
    InitiateFileUploadResponse,
    Error,
    InitiateFileUploadData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: InitiateFileUploadData
      ): Promise<InitiateFileUploadResponse> => {
        const response = await initiateFileUpload({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to initiate file upload');
        }

        return response.data!;
      },
    }));
  }

  // Mutation for committing file upload
  public commitFileUploadMutation(): UseMutationResult<
    CommitFileUploadResponse,
    Error,
    CommitFileUploadData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: CommitFileUploadData
      ): Promise<CommitFileUploadResponse> => {
        const response = await commitFileUpload({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to commit file upload');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate files list and file upload queries
        void this.queryClient.invalidateQueries({
          queryKey: ['files'],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['file-upload'],
        });
      },
    }));
  }

  // Method for getting file download URL
  public async getFileDownloadUrl(
    fileId: string,
    versionId?: string
  ): Promise<string> {
    const downloadData: DownloadUrlData = {
      path: { fileId },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: versionId ? { 'If-Match': versionId } : undefined,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await downloadUrl({
      client: this.client as any,
      ...downloadData,
    });

    if (response.error) {
      throw new Error('Failed to get download URL');
    }

    if (!response.data?.url) {
      throw new Error('Download URL not available');
    }

    return response.data.url;
  }
}
